import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { Button, Container, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/fack.backend";
import AppointmentCard from './appointmentCard';
import CreatePatient from "../../patient/form/create/createPatient";

const STEPS = { UPLOAD: 1, REVIEW: 2 };

const OcrModal = ({ onCloseModal, fetchAppointments }) => {
    const [step, setStep] = useState(STEPS.UPLOAD);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [patients, setPatients] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [insurances, setInsurances] = useState({});
    const [clinics, setClinics] = useState([]);
    const [degrees, setDegrees] = useState([]);
    const [carts, setCarts] = useState({});
    const [procedureOptions, setProcedureOptions] = useState({});
    const [acomodations, setAcomodations] = useState({});
    const [emergencyErrors, setEmergencyErrors] = useState({});
    const [extraFiles, setExtraFiles] = useState({});
    const [isCalculating, setIsCalculating] = useState({});
    const [isOpenCreatePatient, setIsOpenCreatePatient] = useState(false);
    const [createPatientForUid, setCreatePatientForUid] = useState(null);
    const [showPanel, setShowPanel] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [sources, setSources] = useState([]);
    const [sourceProfileId, setSourceProfileId] = useState('');
    const typeaheadRefs = useRef({});
    const fetchPatientsTimer = useRef({});
    const fetchPatientsAbort = useRef({});
    const fetchProceduresTimer = useRef({});
    const fetchProceduresAbort = useRef({});

    const openCreatePatient = (uid) => {
        setCreatePatientForUid(uid);
        setIsOpenCreatePatient(true);
    };

    const closeCreatePatient = () => {
        setIsOpenCreatePatient(false);
        setCreatePatientForUid(null);
    };

    const togglePanel = () => {
        setShowPanel(prev => !prev);
    };

    const handleFile = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        setFile(selected);
        setPreview(URL.createObjectURL(selected));
    };

    const handleProcess = async () => {
        if (!file) return;
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            if (sourceProfileId) formData.append('ocr_source_profile_id', sourceProfileId);
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/ocr/process`,
                formData,
                { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
            );
            const data = response.data.map((item, index) => ({ ...item.resolved, unresolved: item.unresolved, uid: index }));
            setAppointments(data);

            const insurancesMap = {};
            const acomodationsMap = {};
            const cartsMap = {};
            data.forEach((a) => {
                if (a.patient_insurances) {
                    insurancesMap[a.uid] = a.patient_insurances;
                }
                if (a.clinic_acomodations) {
                    acomodationsMap[a.uid] = a.clinic_acomodations;
                }
                if ((a.procedures || []).length > 0) {
                    cartsMap[a.uid] = { procedures: a.procedures };
                }
            });
            setInsurances(insurancesMap);
            setAcomodations(acomodationsMap);
            setCarts(cartsMap);

            setPatients({});
            setProcedureOptions({});
            setEmergencyErrors({});
            setExtraFiles({});
            setIsCalculating({});
            setValidationErrors({});

            setStep(STEPS.REVIEW);
        } catch (err) {
            Swal.fire('Erro', 'Não foi possível processar a imagem. Tente novamente.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const updateAppointment = (uid, field, value) => {
        const unresolvedKeyByField = { patient_id: 'patient', insurance_id: 'insurance', doctor_id: 'doctor' };
        setAppointments(prev => prev.map(item => {
            if (item.uid !== uid) return item;
            const next = { ...item, [field]: value };
            const unresolvedKey = unresolvedKeyByField[field];
            if (unresolvedKey && value && next.unresolved?.includes(unresolvedKey)) {
                next.unresolved = next.unresolved.filter(f => f !== unresolvedKey);
            }
            return next;
        }));
        setValidationErrors(prev => {
            if (!prev[uid]?.includes(field)) return prev;
            const next = { ...prev };
            next[uid] = next[uid].filter(f => f !== field);
            if (next[uid].length === 0) delete next[uid];
            return next;
        });
    };

    const removeAppointment = (uid) => {
        setAppointments(prev => prev.filter(a => a.uid !== uid));

        const descartaCard = (prev) => {
            const next = { ...prev };
            delete next[uid];
            return next;
        };
        setCarts(descartaCard);
        setPatients(descartaCard);
        setInsurances(descartaCard);
        setAcomodations(descartaCard);
        setProcedureOptions(descartaCard);
        setEmergencyErrors(descartaCard);
        setExtraFiles(descartaCard);
        setIsCalculating(descartaCard);
        setValidationErrors(descartaCard);
    };

    const addExtraFiles = (uid, event) => {
        let temp = extraFiles[uid] ? [...extraFiles[uid]] : [];

        Object.values(event.target.files).map(newFile => {
            temp = temp.filter(f => f.name !== newFile.name);
            temp.push(newFile);
        });

        setExtraFiles(prev => ({ ...prev, [uid]: temp }));
        event.target.value = '';
    };

    const removeExtraFile = (uid, name) => {
        setExtraFiles(prev => ({ ...prev, [uid]: (prev[uid] || []).filter(f => f.name !== name) }));
    };

    const fetchPatients = (search = '', uid) => {
        if (fetchPatientsTimer.current[uid]) {
            clearTimeout(fetchPatientsTimer.current[uid]);
        }

        fetchPatientsTimer.current[uid] = setTimeout(async () => {
            if (search.length < 3) return;

            if (fetchPatientsAbort.current[uid]) {
                fetchPatientsAbort.current[uid].abort();
            }
            fetchPatientsAbort.current[uid] = new AbortController();

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/patient/all?search=${search}`,
                    { headers: authHeader(), signal: fetchPatientsAbort.current[uid].signal }
                );
                setPatients(prev => ({ ...prev, [uid]: res.data }));
            } catch (err) {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    console.error('Erro ao buscar pacientes:', err);
                }
            }
        }, 300);
    };

    const fetchDoctors = async () => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/doctor/all`, { headers: authHeader() });
        setDoctors(res.data);
    };

    const fetchInsurancesByPatient = async (patientId, uid) => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/patientInsurance/getInsurances/${patientId}`, { headers: authHeader() });
        setInsurances(prev => ({ ...prev, [uid]: res.data }));
    };

    const fetchClinics = async () => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clinic/getClinics`, { headers: authHeader() });
        setClinics(res.data);
    };

    const fetchDegrees = async () => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/participationDegree`, { headers: authHeader() });
        setDegrees(res.data);
    };

    const fetchSpecialty = async (doctorId, uid) => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/doctor-specialty/${doctorId}`, { headers: authHeader() });
        updateAppointment(uid, 'specialty_id', res.data.id);
        updateAppointment(uid, 'specialty_name', res.data.name);
    };

    const fetchAcomodationsByClinic = async (clinicId, uid) => {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clinicAcomodation/getAcomodations/${clinicId}`, { headers: authHeader() });
        setAcomodations(prev => ({ ...prev, [uid]: res.data }));

        if (res.data.length > 0) {
            const defaultAcomodationId = res.data[0].id;
            updateAppointment(uid, 'acomodation_id', defaultAcomodationId);

            if ((carts[uid]?.procedures || []).length > 0) {
                calculate(uid, carts[uid].procedures, { acomodation_id: defaultAcomodationId });
            }
        }
    };

    const fetchProceduresByInsurance = (insuranceId, search, uid) => {
        if (fetchProceduresTimer.current[uid]) {
            clearTimeout(fetchProceduresTimer.current[uid]);
        }

        fetchProceduresTimer.current[uid] = setTimeout(async () => {
            if (search.length < 3) return;

            if (fetchProceduresAbort.current[uid]) {
                fetchProceduresAbort.current[uid].abort();
            }
            fetchProceduresAbort.current[uid] = new AbortController();

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/v2/procedures/${insuranceId}/by-insurance?search=${search}`,
                    { headers: authHeader(), signal: fetchProceduresAbort.current[uid].signal }
                );
                setProcedureOptions(prev => ({ ...prev, [uid]: res.data }));
            } catch (err) {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    console.error('Erro ao buscar procedimentos:', err);
                }
            }
        }, 300);
    };

    const calculate = async (uid, procedures, overrides = {}) => {
        const appointment = appointments.find(a => a.uid === uid);
        if (!appointment) return;
        const finalAcomodationId = overrides.acomodation_id ?? appointment.acomodation_id;
        if (!finalAcomodationId) return;

        setIsCalculating(prev => ({ ...prev, [uid]: true }));

        const cartPayload = {
            health_insurance_id: appointment.insurance_id,
            acomodation_id: finalAcomodationId,
            degree_percentage: appointment.degree_percentage || 0,
            emergency_percentage: appointment.emergency_percentage || 0,
            acomodation_value: 0,
            procedures,
            ...overrides
        };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v2/appointment/calculate`,
                cartPayload,
                { headers: authHeader() }
            );
            setCarts(prev => ({ ...prev, [uid]: res.data }));
            updateAppointment(uid, 'total', res.data.total);
        } catch (err) {
            console.error('Erro ao calcular procedimentos:', err);
        } finally {
            setIsCalculating(prev => ({ ...prev, [uid]: false }));
        }
    };

    const reloadProcedures = async (uid, insuranceId) => {
        const appt = appointments.find(a => a.uid === uid);
        const readProcs = appt?.read_procedures || [];
        if (!insuranceId) return;

        let emergencyPct = appt?.emergency_percentage || 0;
        if (appt?.is_emergency || appt?.emergency_id) {
            try {
                const em = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/appointment/getEmergency`,
                    { date: appt.date, insurance_id: insuranceId },
                    { headers: authHeader() }
                );
                if (em.data?.id) {
                    updateAppointment(uid, 'emergency_id', em.data.id);
                    updateAppointment(uid, 'emergency_percentage', em.data.percentage);
                    setEmergencyErrors(prev => ({ ...prev, [uid]: null }));
                    emergencyPct = em.data.percentage;
                } else {
                    updateAppointment(uid, 'emergency_id', null);
                    updateAppointment(uid, 'emergency_percentage', 0);
                    setEmergencyErrors(prev => ({ ...prev, [uid]: 'Não existe valor de emergência cadastrado para este convênio nessa data.' }));
                    emergencyPct = 0;
                }
            } catch (err) {
                console.error('Erro ao recalcular emergência:', err);
            }
        }

        if (readProcs.length === 0) return;

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/ocr/match-procedures`,
                { insurance_id: insuranceId, procedures: readProcs },
                { headers: authHeader() }
            );
            const procedures = res.data?.procedures || [];
            const suggestions = res.data?.procedure_suggestions || [];
            updateAppointment(uid, 'procedure_suggestions', suggestions);
            if (procedures.length > 0) {
                setCarts(prev => ({ ...prev, [uid]: { procedures } }));
                calculate(uid, procedures, { health_insurance_id: insuranceId, emergency_percentage: emergencyPct });
            } else {
                setCarts(prev => ({ ...prev, [uid]: { procedures: [] } }));
                updateAppointment(uid, 'total', 0);
            }
        } catch (err) {
            console.error('Erro ao recarregar procedimentos:', err);
        }
    };

    const reloadInsurance = async (uid, patientId, readName) => {
        if (!patientId || !readName) return;
        const appt = appointments.find(a => a.uid === uid);
        if (appt?.insurance_id && !appt?.unresolved?.includes('insurance')) return;

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/ocr/match-insurance`,
                { patient_id: patientId, insurance_name: readName },
                { headers: authHeader() }
            );
            const data = res.data || {};
            updateAppointment(uid, 'insurance_suggestions', data.insurance_suggestions || []);
            if (data.insurance_id) {
                updateAppointment(uid, 'insurance_id', data.insurance_id);
                updateAppointment(uid, 'insurance_name', data.insurance_name);
                reloadProcedures(uid, data.insurance_id);
            }
        } catch (err) {
            console.error('Erro ao recasar convênio:', err);
        }
    };

    const emergencyToggle = async (uid) => {
        const appointment = appointments.find(a => a.uid === uid);
        if (!appointment) return;

        if (appointment.emergency_id) {
            updateAppointment(uid, 'emergency_id', null);
            updateAppointment(uid, 'emergency_percentage', 0);
            setEmergencyErrors(prev => ({ ...prev, [uid]: null }));
            if ((carts[uid]?.procedures || []).length > 0) {
                calculate(uid, carts[uid].procedures, { emergency_percentage: 0 });
            }
            return;
        }

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/appointment/getEmergency`,
                { date: appointment.date, insurance_id: appointment.insurance_id },
                { headers: authHeader() }
            );

            if (res.data?.id) {
                updateAppointment(uid, 'emergency_id', res.data.id);
                updateAppointment(uid, 'emergency_percentage', res.data.percentage);
                setEmergencyErrors(prev => ({ ...prev, [uid]: null }));
                if ((carts[uid]?.procedures || []).length > 0) {
                    calculate(uid, carts[uid].procedures, { emergency_percentage: res.data.percentage });
                }
            } else {
                setEmergencyErrors(prev => ({ ...prev, [uid]: 'Não existe valor de emergência cadastrado para este convênio nessa data.' }));
            }
        } catch (err) {
            setEmergencyErrors(prev => ({ ...prev, [uid]: 'Erro ao verificar emergência.' }));
        }
    };

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/ocr/sources`, { headers: authHeader() })
            .then(res => setSources(res.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (step === STEPS.REVIEW) {
            fetchDoctors();
            fetchClinics();
            fetchDegrees();
            appointments.forEach((a) => {
                if (a.patient_id) {
                    setPatients(prev => ({ ...prev, [a.uid]: [{ id: a.patient_id, name: a.patient_name }] }));
                } else if (a.patient_name && a.patient_name.length >= 3) {
                    fetchPatients(a.patient_name, a.uid);
                }
                if ((carts[a.uid]?.procedures || []).length > 0 && a.insurance_id && a.acomodation_id) {
                    calculate(a.uid, carts[a.uid].procedures);
                }
            });
        }
    }, [step]);

    const handleSave = async () => {
        const required = ['patient_id', 'insurance_id', 'doctor_id', 'degree_id', 'clinic_id', 'date', 'time'];
        const errors = {};
        appointments.forEach((a) => {
            const missing = required.filter(f => !a[f]);
            if (missing.length > 0) errors[a.uid] = missing;
        });

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            const firstUid = Object.keys(errors)[0];
            document.querySelector(`[data-appointment-uid="${firstUid}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        setValidationErrors({});

        Swal.fire({
            title: 'Salvando agendamentos...',
            html: 'Por favor aguarde!',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        let uploads = [];
        if (file) {
            try {
                uploads = await Promise.all(appointments.map((a) => {
                    const formData = new FormData();
                    formData.append('files[0]', file);
                    (extraFiles[a.uid] || []).map((extra, extraIndex) => {
                        formData.append(`files[${extraIndex + 1}]`, extra);
                    });
                    return axios.post(
                        `${process.env.REACT_APP_API_URL}/api/appointment/attachment`,
                        formData,
                        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
                    );
                }));
            } catch (err) {
                Swal.fire('Erro', 'Não foi possível enviar os anexos. Tente salvar novamente.', 'error');
                return;
            }
        }

        try {
            await Promise.all(appointments.map((a, index) =>
                axios.post(
                    `${process.env.REACT_APP_API_URL}/api/appointment/create`,
                    {
                        date: a.date,
                        time: a.time,
                        appointmentType: a.appointment_type,
                        observation: a.observation,
                        guideNumber: a.guide_number,
                        insurance_id: a.insurance_id,
                        patient_id: a.patient_id,
                        doctor_id: a.doctor_id,
                        clinic_id: a.clinic_id,
                        acomodation_id: a.acomodation_id,
                        degree_id: a.degree_id,
                        specialty_id: a.specialty_id,
                        emergency_id: a.emergency_id || null,
                        total: a.total || 0,
                        procedures: carts[a.uid]?.procedures || [],
                        files: uploads[index] ? uploads[index].data : []
                    },
                    { headers: authHeader() }
                )
            ));

            Swal.fire('Sucesso', `${appointments.length} agendamento(s) criado(s) com sucesso!`, 'success');
            fetchAppointments();
            onCloseModal();
        } catch (err) {
            Swal.fire('Erro', 'Não foi possível salvar um ou mais agendamentos.', 'error');
        }
    };

    return (
        <>
            <ModalHeader toggle={onCloseModal}>
                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <h5 className="mb-0">
                        {showPanel
                            ? 'OCR — Painel de Uso'
                            : step === STEPS.UPLOAD
                                ? 'OCR — Processar Imagem'
                                : `OCR — Revisar Dados (${appointments.length} agendamento(s))`}
                    </h5>
                    <span className="ocr-panel-btn" onClick={togglePanel}>
                        {showPanel ? '← Voltar' : 'ℹ Painel'}
                    </span>
                </div>
                {!showPanel && step === STEPS.REVIEW && (
                    <small className="text-muted d-block mt-1 ocr-header-legend">
                        <span className="ocr-legend-mark-success">■</span> Alta confiança &nbsp;
                        <span className="ocr-legend-mark-warning">■</span> Confiança média — revise &nbsp;
                        <span className="ocr-legend-mark-danger">■</span> Não encontrado / baixa confiança
                    </small>
                )}
            </ModalHeader>

            <ModalBody className="ocr-modal-body">
                <Container>

                    {/* PAINEL */}
                    {showPanel && (
                        <div className="ocr-panel">

                            <div className="ocr-panel-section">
                                <p className="ocr-panel-section-title">Como Funciona</p>
                                <div className="ocr-info-steps">
                                    <div className="ocr-info-step">
                                        <span className="ocr-info-step-num">1</span>
                                        <div>Escolha a <strong>Origem (clínica)</strong> e envie a foto ou print — é a origem que define como o documento será lido.</div>
                                    </div>
                                    <div className="ocr-info-step">
                                        <span className="ocr-info-step-num">2</span>
                                        <div>A imagem é enviada ao <strong>Claude (Anthropic)</strong>, que lê e identifica cada agendamento — uma imagem pode conter vários.</div>
                                    </div>
                                    <div className="ocr-info-step">
                                        <span className="ocr-info-step-num">3</span>
                                        <div>O sistema busca os dados no banco e pré-preenche os cards: <strong>verde</strong> = encontrado, <strong>azul</strong> = sugestão (confirme com 1 clique), <strong>vermelho</strong> = não encontrado (busque ou cadastre).</div>
                                    </div>
                                    <div className="ocr-info-step">
                                        <span className="ocr-info-step-num">4</span>
                                        <div>Revise os cards e <strong>salve</strong> — nada é gravado no sistema antes de você confirmar.</div>
                                    </div>
                                </div>
                            </div>

                            <div className="ocr-panel-section">
                                <p className="ocr-panel-section-title">O que é Extraído</p>
                                <p className="text-muted small mb-2">Por agendamento encontrado na imagem:</p>
                                <div className="ocr-info-tags">
                                    {['Paciente', 'Convênio', 'Médico', 'Data', 'Hora', 'Tipo', 'N° Guia', 'Procedimentos', 'Grau de Participação', 'Emergência'].map(tag => (
                                        <span key={tag} className="ocr-info-tag">{tag}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="ocr-panel-section">
                                <p className="ocr-panel-section-title">Dicas</p>
                                <ul className="ocr-info-list">
                                    <li>O <strong>convênio</strong> carrega depois de escolher o paciente; os <strong>procedimentos</strong>, depois do convênio</li>
                                    <li><strong>Data e hora não lidas ficam vazias</strong> de propósito — preencha antes de salvar</li>
                                    <li>Linhas com <strong>Faltou/Desmarcou</strong> são descartadas automaticamente</li>
                                    <li>Prefira imagens <strong>nítidas</strong>; prints de tela funcionam melhor que fotos de papel</li>
                                    <li>Limites: <strong>JPG, JPEG, PNG</strong> — até <strong>7 MB</strong> — <strong>50 processamentos</strong> por dia</li>
                                </ul>
                            </div>

                        </div>
                    )}

                    {/* STEP 1 */}
                    {!showPanel && step === STEPS.UPLOAD && (
                        <div>
                            <p className="text-muted mb-2">Selecione a foto ou print do agendamento para processar.</p>
                            <small className="text-muted d-block mb-3">
                                Formatos aceitos: JPG, JPEG, PNG — Tamanho máximo: 7MB
                            </small>
                            <div className="form-group mb-3">
                                <label>Origem (clínica)</label>
                                <Input type="select" value={sourceProfileId} onChange={(e) => setSourceProfileId(e.target.value)}>
                                    <option value="">Selecione a origem…</option>
                                    {sources.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Input>
                            </div>
                            <Input type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleFile} />
                            {preview && (
                                <div className="mt-3 text-center">
                                    <img src={preview} alt="preview" className="ocr-preview-image" />
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 2 */}
                    {!showPanel && step === STEPS.REVIEW && appointments.map((appointment, index) => (
                        <AppointmentCard
                            key={appointment.uid}
                            appointment={appointment}
                            uid={appointment.uid}
                            position={index}
                            cart={carts[appointment.uid]}
                            patientOptions={patients[appointment.uid] || []}
                            insuranceOptions={insurances[appointment.uid] || []}
                            acomodationOptions={acomodations[appointment.uid] || []}
                            procedureOptions={procedureOptions[appointment.uid] || []}
                            emergencyError={emergencyErrors[appointment.uid]}
                            extraFiles={extraFiles[appointment.uid] || []}
                            ocrFileName={file ? file.name : ''}
                            addExtraFiles={addExtraFiles}
                            removeExtraFile={removeExtraFile}
                            isCalculating={!!isCalculating[appointment.uid]}
                            doctors={doctors}
                            clinics={clinics}
                            degrees={degrees}
                            typeaheadRefs={typeaheadRefs}
                            updateAppointment={updateAppointment}
                            removeAppointment={removeAppointment}
                            fetchPatients={fetchPatients}
                            fetchInsurancesByPatient={fetchInsurancesByPatient}
                            fetchSpecialty={fetchSpecialty}
                            fetchAcomodationsByClinic={fetchAcomodationsByClinic}
                            fetchProceduresByInsurance={fetchProceduresByInsurance}
                            calculate={calculate}
                            reloadProcedures={reloadProcedures}
                            reloadInsurance={reloadInsurance}
                            emergencyToggle={emergencyToggle}
                            setInsurances={setInsurances}
                            setAcomodations={setAcomodations}
                            openCreatePatient={openCreatePatient}
                            validationError={validationErrors[appointment.uid]}
                        />
                    ))}

                </Container>
            </ModalBody>

            <ModalFooter>
                {showPanel && (
                    <Button color="secondary" onClick={togglePanel}>Fechar Painel</Button>
                )}
                {!showPanel && step === STEPS.UPLOAD && (
                    <>
                        <Button color="secondary" onClick={onCloseModal}>Cancelar</Button>
                        <Button color="primary" onClick={handleProcess} disabled={!file || isLoading}>
                            {isLoading ? <><Spinner size="sm" /> Processando...</> : 'Processar Imagem'}
                        </Button>
                    </>
                )}
                {!showPanel && step === STEPS.REVIEW && (
                    <>
                        <Button color="secondary" onClick={() => setStep(STEPS.UPLOAD)}>Voltar</Button>
                        <Button color="success" onClick={handleSave}>
                            Salvar {appointments.length} Agendamento(s)
                        </Button>
                    </>
                )}
            </ModalFooter>

            <Modal isOpen={isOpenCreatePatient} size="lg">
                <CreatePatient
                    onCloseModal={closeCreatePatient}
                    fetchPatients={() => {
                        if (createPatientForUid !== null) {
                            setPatients(prev => ({ ...prev, [createPatientForUid]: [] }));
                        }
                    }}
                />
            </Modal>
        </>
    );
};

export default OcrModal;
